import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import confirmImage from 'src/assets/imgs/confirmImage.png';
import { cancelSentWalletApprovalRequest } from 'src/services/wallet';

export type CancelSentWalletApprovalDialogProps = {
  open: boolean;
  onClose: () => void;
  walletId: string;
  walletName: string;
  onSuccess?: () => void;
  invalidateQueries?: string[][];
};

export function CancelSentWalletApprovalDialog({
  open,
  onClose,
  walletId,
  walletName,
  onSuccess,
  invalidateQueries = [],
}: CancelSentWalletApprovalDialogProps) {
  const queryClient = useQueryClient();

  const {
    mutate: sendCancelSentRequestPendingWalletMutation,
    isLoading: isLoadingSendCancelSentRequestPendingWallet,
  } = useMutation({
    mutationFn: (id: string) =>
      cancelSentWalletApprovalRequest({ walletId: id, status: 'pending' }),
    onSuccess: () => {
      toast.success('Solicitação de cancelamento enviada com sucesso');
      onClose();

      // Invalida as queries fornecidas
      invalidateQueries.forEach((queryKey) => {
        queryClient.invalidateQueries(queryKey);
      });

      // Callback customizado de sucesso
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const handleClose = () => {
    if (!isLoadingSendCancelSentRequestPendingWallet) {
      onClose();
    }
  };

  const handleConfirm = () => {
    sendCancelSentRequestPendingWalletMutation(walletId);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description">
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'space-evenly',
          padding: '32px 20px 0',
          overflow: 'hidden',
          minHeight: '465px',
        }}>
        <img src={confirmImage} width={185} height={185} alt="Confirmação" />
        <Typography
          sx={{ padding: '0px 64px' }}
          variant="h4"
          textAlign="center">
          Cancelar envio de solicitação.
        </Typography>
        <Typography
          maxWidth={430}
          textAlign="center"
          sx={{ fontSize: '0.875rem' }}>
          Tem certeza que deseja cancelar o envio da solicitação para a&nbsp;
          aprovação da carteira <strong>{walletName}</strong>?
        </Typography>
      </DialogContent>
      <DialogActions
        sx={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 0 40px 0',
          gap: 2,
        }}
        disableSpacing>
        <Button
          size="large"
          variant="outlined"
          color="secondary"
          onClick={handleClose}
          disabled={isLoadingSendCancelSentRequestPendingWallet}
          sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
          Não, manter solicitação.
        </Button>
        <Button
          size="large"
          variant="contained"
          onClick={handleConfirm}
          disabled={isLoadingSendCancelSentRequestPendingWallet}
          sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
          {isLoadingSendCancelSentRequestPendingWallet
            ? 'Cancelando...'
            : 'Sim, cancelar envio!'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
